import type { Components } from "react-markdown";
import { CodeBlock } from "@/components/CodeBlock";

// react-markdown component overrides used by cell renderers for prose blocks.
// Inline headings inside a cell render as h3/h4 so they don't collide with the
// cell's own header (cells own the h2 slot in the lesson document outline).
export const lessonMarkdownComponents: Components = {
  // react-markdown v9 no longer passes an `inline` prop, and rehype-sanitize
  // strips the `language-X` className from <code> elements (the default schema
  // doesn't allow it). So we detect fenced blocks by their content: a fenced
  // block always has at least one newline; inline code never does.
  code({ className, children }) {
    const text = String(children ?? "");
    if (!text.includes("\n")) {
      return (
        <code className="rounded bg-muted px-1 py-0.5 text-xs font-mono">{children}</code>
      );
    }
    const langMatch = /language-(\w+)/.exec(className ?? "");
    return (
      <CodeBlock
        code={text.replace(/\n$/, "")}
        language={langMatch?.[1] ?? "python"}
      />
    );
  },
  // Demote headings so they don't collide with the cell's own header.
  h1: ({ children }) => <h3 className="text-xl font-semibold mt-4 mb-2">{children}</h3>,
  h2: ({ children }) => <h3 className="text-xl font-semibold mt-4 mb-2">{children}</h3>,
  h3: ({ children }) => <h4 className="text-lg font-semibold mt-3 mb-2">{children}</h4>,
  p:  ({ children }) => <p className="text-base text-foreground leading-relaxed my-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-inside space-y-1 my-2 text-base text-foreground">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside space-y-1 my-2 text-base text-foreground">{children}</ol>,
  strong: ({ children }) => <strong className="text-foreground">{children}</strong>,
  a: ({ children, href }) => (
    <a href={href} className="text-primary underline hover:text-primary-soft">{children}</a>
  )
};
